import React, { useEffect, useState } from 'react';
import Button from '@material-ui/core/Button';
import { makeStyles } from '@material-ui/core/styles';
import MenuItem from '@material-ui/core/MenuItem';
import TextField from '@material-ui/core/TextField';
import 'date-fns';
import Alert from '@material-ui/lab/Alert';
import EventDataService from '../../services/EventService';
import DevicesDataService from '../../services/DeviceService';
import UsersDataService from '../../services/UserService';
import Autocomplete from '@material-ui/lab/Autocomplete';
import { Grid } from '@material-ui/core';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';
import { useTranslation } from 'react-i18next';
import { getAppTimezone } from '../../utils/timezone';
import DateTimePickerWithManualInput from '../common/DateTimePickerWithManualInput';
import { getDateFormatAndLocale } from '../../utils/reportDateFormat';
import { buildEventRowFromCreate } from '../../utils/eventRowUtils';
import { getAdminIdFromCookie } from '../../utils/jwtUtils';

dayjs.extend(utc);
dayjs.extend(timezone);

const initialEventState = {
    id: null,
    device_mac: '',
    user_id: '',
    location: '',
    time: '',
    event: '',
    genuine: false,
};

const useStyles = makeStyles(theme => ({
    root: {
        width: '100%',
        '& .MuiTextField-root': {
            width: '100%',
        },
        '& .MuiFormControl-root': {
            width: '100%',
        },
        '& .MuiAlert-root': {
            width: '100%',
        },
    },
    field: {
        width: '100%',
    },
    alert: {
        marginBottom: theme.spacing(2),
    },
}));

const AddEventForm = ({
    onEventCreated,
    onSubmittingChange,
    formId,
    hideSubmitButton,
    resetKey,
}) => {
    const { t, i18n } = useTranslation();
    const dateConfig = getDateFormatAndLocale(i18n.language);
    const classes = useStyles();

    const [devices, setDevices] = useState([]);
    const [users, setUsers] = useState([]);
    const [manualEvent, setEvent] = useState(initialEventState);
    const [selectedDate, handleDateChange] = useState(
        dayjs().tz(getAppTimezone())
    );
    const [selectedUser, setSelectedUser] = useState(null);
    const [inputUserValue, setInputUserValue] = useState('');
    const [fieldErrors, setFieldErrors] = useState({});
    const [submitError, setSubmitError] = useState(null);
    const [isGenerating, setIsGenerating] = useState(false);

    const level = localStorage.getItem('level');
    const isVideolux = window.location.hostname === 'videolux.biodit.bg';
    const currentDate = dayjs()
        .tz(getAppTimezone())
        .subtract(isVideolux ? 1 : 7, isVideolux ? 'month' : 'day')
        .startOf('day');
    const endOfDay = dayjs().tz(getAppTimezone()).endOf('day');
    const isAdmin = level === 'super' || level === 'admin';

    useEffect(() => {
        retrieveDevices();
        retrieveUsers();
    }, []);

    useEffect(() => {
        if (resetKey === undefined) {
            return;
        }
        setEvent(initialEventState);
        setSelectedUser(null);
        setInputUserValue('');
        handleDateChange(dayjs().tz(getAppTimezone()));
        setFieldErrors({});
        setSubmitError(null);
        setIsGenerating(false);
    }, [resetKey]);

    useEffect(() => {
        if (onSubmittingChange) {
            onSubmittingChange(isGenerating);
        }
    }, [isGenerating, onSubmittingChange]);

    const retrieveDevices = async () => {
        const locData = JSON.parse(localStorage.getItem('loc'));

        try {
            let response;

            if (Array.isArray(locData)) {
                const joinedValues = locData.join(',');
                response = await DevicesDataService.getAll(joinedValues);
            } else if (locData && locData.id === 'all') {
                response = await DevicesDataService.adminAll();
            }

            if (response) {
                setDevices(response.data);
            }
        } catch (e) {
            console.error(e);
        }
    };

    const retrieveUsers = async () => {
        const locData = JSON.parse(localStorage.getItem('loc'));

        try {
            let response;

            if (Array.isArray(locData)) {
                const joinedValues = locData.join(',');
                response = await UsersDataService.getAll(joinedValues);
            } else if (locData && locData.id === 'all') {
                response = await UsersDataService.adminAll();
            }

            if (response) {
                setUsers(response.data);
            }
        } catch (e) {
            console.error(e);
        }
    };

    const clearFieldError = field => {
        setFieldErrors(prev => {
            if (!prev[field]) {
                return prev;
            }
            const next = { ...prev };
            delete next[field];
            return next;
        });
    };

    const validateForm = () => {
        const errors = {};

        if (!manualEvent.device_mac) {
            errors.device = t('reports.eventsReport.addEvent.validationDevice');
        }
        if (!manualEvent.event) {
            errors.eventType = t(
                'reports.eventsReport.addEvent.validationEventType'
            );
        }
        if (!selectedUser) {
            errors.employee = inputUserValue.trim()
                ? t('reports.eventsReport.addEvent.validationEmployeeSelect')
                : t('reports.eventsReport.addEvent.validationEmployee');
        }

        return errors;
    };

    const handleInputChange = event => {
        const { name, value } = event.target;
        setEvent({ ...manualEvent, [name]: value });
        if (name === 'device_mac') {
            clearFieldError('device');
        }
        if (name === 'event') {
            clearFieldError('eventType');
        }
        setSubmitError(null);
    };

    const selectedDevice = devices.find(
        device => device.mac === manualEvent.device_mac
    );

    const selectedDeviceLocation = selectedDevice
        ? selectedDevice.location
        : null;

    const saveEvent = e => {
        if (e && e.preventDefault) {
            e.preventDefault();
        }

        const errors = validateForm();
        if (Object.keys(errors).length > 0) {
            setFieldErrors(errors);
            setSubmitError(null);
            return;
        }

        const adminId = getAdminIdFromCookie();
        if (!adminId) {
            setSubmitError(t('reports.eventsReport.addEvent.authError'));
            return;
        }

        setFieldErrors({});
        setSubmitError(null);
        setIsGenerating(true);

        const data = {
            device_mac: manualEvent.device_mac,
            user_id: selectedUser.sap,
            location: selectedDeviceLocation,
            time: selectedDate,
            event: manualEvent.event,
            genuine: manualEvent.genuine,
            adminId,
        };

        EventDataService.create(data)
            .then(response => {
                setIsGenerating(false);
                if (onEventCreated) {
                    onEventCreated(
                        buildEventRowFromCreate({
                            created: response.data,
                            user: selectedUser,
                            device: selectedDevice,
                        })
                    );
                }
            })
            .catch(err => {
                console.error(err);
                setIsGenerating(false);
                const message =
                    (err.response && err.response.data && err.response.data.message) ||
                    t('reports.eventsReport.addEvent.createFailed');
                setSubmitError(message);
            });
    };

    return (
        <form
            id={formId}
            className={classes.root}
            onSubmit={saveEvent}
            noValidate
        >
            {submitError && (
                <Alert severity="error" className={classes.alert}>
                    {submitError}
                </Alert>
            )}
            <Grid container spacing={2}>
                <Grid item xs={12} sm={6}>
                    <TextField
                        className={classes.field}
                        id="device_mac"
                        select
                        label={t('reports.eventsReport.addEvent.device')}
                        name="device_mac"
                        value={manualEvent.device_mac}
                        onChange={handleInputChange}
                        size="small"
                        variant="outlined"
                        required
                        error={Boolean(fieldErrors.device)}
                        helperText={fieldErrors.device || ''}
                    >
                        <MenuItem value="" />
                        {devices.map(device => (
                            <MenuItem key={device.mac} value={device.mac}>
                                {device.name}
                            </MenuItem>
                        ))}
                    </TextField>
                </Grid>
                <Grid item xs={12} sm={6}>
                    <Autocomplete
                        className={classes.field}
                        value={selectedUser}
                        onChange={(event, newValue) => {
                            setSelectedUser(newValue);
                            clearFieldError('employee');
                            setSubmitError(null);
                        }}
                        inputValue={inputUserValue}
                        onInputChange={(event, newInputValue) => {
                            setInputUserValue(newInputValue);
                            if (selectedUser) {
                                clearFieldError('employee');
                            }
                        }}
                        options={users}
                        getOptionLabel={option =>
                            (option && option.name + ' - ' + option.sap) || ''
                        }
                        renderInput={params => (
                            <TextField
                                {...params}
                                label={t(
                                    'reports.eventsReport.addEvent.employee'
                                )}
                                required
                                variant="outlined"
                                size="small"
                                error={Boolean(fieldErrors.employee)}
                                helperText={fieldErrors.employee || ''}
                            />
                        )}
                    />
                </Grid>
                <Grid item xs={12} sm={6}>
                    <TextField
                        className={classes.field}
                        id="event"
                        select
                        label={t('reports.eventsReport.addEvent.eventType')}
                        name="event"
                        value={manualEvent.event}
                        onChange={handleInputChange}
                        size="small"
                        variant="outlined"
                        required
                        error={Boolean(fieldErrors.eventType)}
                        helperText={fieldErrors.eventType || ''}
                    >
                        <MenuItem value="" />
                        <MenuItem value="enter">
                            {t('reports.eventsReport.addEvent.enter')}
                        </MenuItem>
                        <MenuItem value="exit">
                            {t('reports.eventsReport.addEvent.exit')}
                        </MenuItem>
                    </TextField>
                </Grid>
                <Grid item xs={12} sm={6}>
                    <DateTimePickerWithManualInput
                        label={t('reports.eventsReport.addEvent.dateandtime')}
                        value={selectedDate}
                        onChange={handleDateChange}
                        format={dateConfig.format}
                        locale={dateConfig.locale}
                        size="small"
                        variant="outlined"
                        fullWidth
                        minDateTime={isAdmin ? undefined : currentDate}
                        maxDateTime={isAdmin ? undefined : endOfDay}
                        disableFuture={!isAdmin}
                    />
                </Grid>
                {!hideSubmitButton && (
                    <Grid item xs={12}>
                        <Button
                            type="submit"
                            variant="contained"
                            color="primary"
                            disabled={isGenerating}
                        >
                            {isGenerating
                                ? t('reports.eventsReport.addEvent.adding')
                                : t('reports.eventsReport.addEvent.add')}
                        </Button>
                    </Grid>
                )}
            </Grid>
        </form>
    );
};

export default AddEventForm;
